// src/pages/Services.jsx
import {
    Container,
    Typography,
    Box,
    Paper,
    Button,
    Grid,
    keyframes,
} from "@mui/material";

import DownhillSkiingIcon from "@mui/icons-material/DownhillSkiing";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import GroupsIcon from "@mui/icons-material/Groups";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

export default function Services() {
    return (
        <Container maxWidth="lg" sx={{ mt: 6, mb: 10 }}>

            {/* HERO */}
            <Box
                sx={{
                    position: "relative",
                    height: {
                        xs: "auto",   // 🔥 laisse la hauteur s’adapter au texte
                        md: 320,      // desktop reste fixe
                    },
                    py: { xs: 6, md: 0 }, // 🔥 ajoute du padding vertical en mobile

                    borderRadius: 3,
                    overflow: "hidden",
                    mb: 8,
                    animation: `${fadeInUp} 0.5s ease-out`,
                }}
            >
                <Box
                    component="img"
                    src="/img_1.png"
                    alt="Photo finish ski"
                    sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        filter: "brightness(0.7)",
                    }}
                />

                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                            "linear-gradient(135deg, rgba(15,42,74,0.7), rgba(26,77,143,0.4))",
                    }}
                />

                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        textAlign: "center",
                        color: "white",
                        px: 2,
                    }}
                >
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 700,
                            mb: 1,
                            textShadow: "0 3px 10px rgba(0,0,0,0.4)",
                        }}
                    >
                        Nos prestations
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 400,
                            opacity: 0.9,
                            textShadow: "0 2px 6px rgba(0,0,0,0.4)",
                        }}
                    >
                        Un chronométrage professionnel pour tous vos événements sportifs.
                    </Typography>
                </Box>
            </Box>

            {/* INTRO */}
            <Box
                sx={{
                    mb: 6,
                    textAlign: "center",
                    animation: `${fadeInUp} 0.7s ease-out`,
                }}
            >
                <Typography
                    variant="body1"
                    sx={{
                        maxWidth: "720px",
                        margin: "0 auto",
                        color: "text.secondary",
                    }}
                >
                    Nous proposons des solutions de chronométrage adaptées à tous les sports :
                    ski, telemark, course à pied, vélo, concours par équipes et événements
                    multi‑disciplines.
                </Typography>
            </Box>

            {/* SERVICES GRID */}
            <Grid
                container
                spacing={4}
                sx={{
                    mb: 8,
                    alignItems: "stretch", // 🔹 force les cards à avoir la même hauteur
                }}
            >
                {/* SKI */}
                <Grid
                    container
                    direction="column"
                    spacing={4}                 // 🔥 espace entre les cards
                    alignItems="center"         // 🔥 centre horizontalement
                    sx={{
                        width: {
                            xs: "100%",             // mobile
                            sm: "80%",              // écran normal
                            lg: "40%",              // grand écran
                        },
                        minWidth: {
                            sm: 600,                // largeur minimale dès sm
                            lg: 600,
                        },
                        margin: "0 auto",
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            height: "100%", // 🔹 occupe toute la hauteur disponible
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between", // 🔹 équilibre le contenu verticalement
                            animation: `${fadeInUp} 0.8s ease-out`,
                        }}
                    >
                        <Box
                            sx={{
                                width: 50,
                                height: 50,
                                borderRadius: "50%",
                                backgroundColor: "primary.main",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 2,
                            }}
                        >
                            <DownhillSkiingIcon sx={{ color: "white" }} />
                        </Box>

                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                            Chronométrage ski & telemark
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Du simple événement d’entreprise aux compétitions internationales,
                            nous assurons un chronométrage précis et fiable pour toutes les
                            disciplines de ski alpin et de telemark.
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{ fontStyle: "italic", color: "text.secondary" }}
                        >
                            De la course populaire à la Coupe du Monde de Telemark.
                        </Typography>
                    </Paper>
                </Grid>

                {/* COURSE À PIED / VÉLO */}

                <Grid
                    container
                    direction="column"
                    spacing={4}                 // 🔥 espace entre les cards
                    alignItems="center"         // 🔥 centre horizontalement
                    sx={{
                        width: {
                            xs: "100%",             // mobile
                            sm: "80%",              // écran normal
                            lg: "40%",              // grand écran
                        },
                        minWidth: {
                            sm: 600,                // largeur minimale dès sm
                            lg: 600,
                        },
                        margin: "0 auto",
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            animation: `${fadeInUp} 0.9s ease-out`,
                        }}
                    >
                        <Box
                            sx={{
                                width: 50,
                                height: 50,
                                borderRadius: "50%",
                                backgroundColor: "primary.main",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 2,
                            }}
                        >
                            <DirectionsRunIcon sx={{ color: "white" }} />
                        </Box>

                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                            Courses à pied & vélo
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Technologie Ubidium RaceResult pour chronométrer efficacement les
                            courses à pied, trails, VTT et compétitions cyclistes.
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{ fontStyle: "italic", color: "text.secondary" }}
                        >
                            Une technologie fiable pour des milliers de participants.
                        </Typography>
                    </Paper>
                </Grid>

                {/* CONCOURS PAR ÉQUIPES */}
                <Grid
                    container
                    direction="column"
                    spacing={4}                 // 🔥 espace entre les cards
                    alignItems="center"         // 🔥 centre horizontalement
                    sx={{
                        width: {
                            xs: "100%",             // mobile
                            sm: "80%",              // écran normal
                            lg: "40%",              // grand écran
                        },
                        minWidth: {
                            sm: 600,                // largeur minimale dès sm
                            lg: 600,
                        },
                        margin: "0 auto",
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            animation: `${fadeInUp} 1.0s ease-out`,
                        }}
                    >
                        <Box
                            sx={{
                                width: 50,
                                height: 50,
                                borderRadius: "50%",
                                backgroundColor: "primary.main",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 2,
                            }}
                        >
                            <GroupsIcon sx={{ color: "white" }} />
                        </Box>

                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                            Concours par équipes
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Formats personnalisés, classements cumulés, gestion complète des
                            résultats. Parfait pour des événements comme le Swiss Snow Happening.
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{ fontStyle: "italic", color: "text.secondary" }}
                        >
                            Une gestion flexible pour des compétitions ludiques ou professionnelles.
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>


            {/* CTA */}
            {/* SECTION — Contact (version card premium) */}
            <Grid
                container
                direction="column"
                alignItems="center"
                sx={{
                    width: "100%",
                    maxWidth: 700,
                    margin: "0 auto",
                    px: 2,
                    mt: 10,
                }}
            >
                <Grid item xs={12} sx={{ width: "100%" }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            mb: 4,
                            animation: `${fadeInUp} 0.8s ease-out`,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    backgroundColor: "secondary.light",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mr: 2,
                                }}
                            >
                            <GroupsIcon sx={{ color: "white" }} />
                        </Box>
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                            Vous êtes à la recherche d'un partenaire chronométrage?
                        </Typography>

                        <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
                            Contactez‑nous pour une offre personnalisée ou pour toute question.
                        </Typography>

                        <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            href="mailto:info@321-timing.ch"
                        >
                            Nous contacter
                        </Button>
                    </Paper>
                </Grid>
            </Grid>

        </Container>
    );
}
